import { Bell, PanelLeftDashed, Star } from 'lucide-react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  useLocalStorage,
  useMediaQuery,
  useReadLocalStorage,
} from 'usehooks-ts';

import { mainNavigationList, secondaryNavigationList } from '@/utils/data/nav';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { SearchInput } from '../ui/searchInput';
import { SidebarDrawer } from './SidebarDrawer';

interface NavbarProps {
  // Still passed by callers, currently unused inside Navbar.
  setSideOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Navbar = ({ setNavOpen }: NavbarProps) => {
  const location = useLocation();

  const matches = useMediaQuery('(min-width: 1080px)');
  const [value, setValue] = useLocalStorage<
    {
      name: string;
      path: string;
    }[]
  >('favourite-pages', []);

  const favoritePages = useReadLocalStorage('favourite-pages') as {
    name: string;
    path: string;
  }[];

  const isCurrentPageFavorite = favoritePages?.some(
    (item) => item.path === location.pathname,
  );

  const allNavList = [...mainNavigationList, ...secondaryNavigationList];

  function findMenuItemByPath(items: any, targetPath: string) {
    for (const item of items) {
      // Check if the current item matches the path
      if (item.path === targetPath) {
        return { primaryPathName: item.label, path: item.path };
      }

      // If the item has subItems, search recursively in the subItems array
      if (item.subItems && item.subItems.length > 0) {
        const result = findMenuItemByPath(item.subItems, targetPath) as any;

        if (result) {
          return {
            primaryPathname: item.label,
            secondaryPathname: result.primaryPathName,
            path: result.path,
          };
        }
      }
    }
    // Return null if no match is found
    return null;
  }

  const handleUpdateFavoriteStatus = () => {
    if (isCurrentPageFavorite) {
      const updatedFavoritePages = favoritePages?.filter(
        (item) => item.path !== location.pathname,
      );
      setValue(updatedFavoritePages);
    } else {
      const updatedFavoritePages = [
        ...(value ?? []),
        { name: location.pathname, path: location.pathname },
      ];
      setValue(updatedFavoritePages);
    }
  };

  return (
    <div className="flex w-full flex-col bg-zinc-50 ">
      <header className="flex  flex-row  justify-between items-center border-b border-b-white/10 bg-background py-5 px-7 h-16 w-full ">
        <div className="flex items-center gap-6">
          {matches ? (
            <PanelLeftDashed
              className="cursor-pointer"
              onClick={() => {
                setNavOpen((v) => !v);
              }}
              size={14}
            />
          ) : (
            <SidebarDrawer />
          )}
          <Star
            className="cursor-pointer"
            onClick={handleUpdateFavoriteStatus}
            fill={isCurrentPageFavorite ? 'yellow' : ''}
            size={14}
          />
          <div className="flex items-center gap-x-2">
            <Link to="/" className="text-sm dark:text-white/40">
              {findMenuItemByPath(allNavList, location.pathname)
                ?.primaryPathname ?? 'Dashboard'}
            </Link>
            <p className="dark:text-white/20">/</p>
            <Link to="/" className="text-sm">
              {findMenuItemByPath(allNavList, location.pathname)
                ?.secondaryPathname ?? 'Default'}
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <SearchInput />
          <Bell size={20} />
          <Avatar className="h-7 w-7 cursor-pointer">
            <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
            <AvatarFallback>CN</AvatarFallback>
          </Avatar>
        </div>
      </header>
    </div>
  );
};

export default Navbar;
